/**
 * Mensagens de UMA conversa (o painel é remontado a cada conversa aberta):
 * a última página ao abrir, as anteriores sob demanda e, enquanto a conversa
 * está aberta e a aba visível, a última página de novo a cada 10 s para as
 * mensagens novas (inclusive as que a Central mandou e voltaram pelo monitor).
 * `GET /mensagens?conversa=ID&antes=ISO` → `{mensagens, mais}`.
 *
 * `motivo` diz à tela o que fazer com a rolagem: "inicial" rola para o fim;
 * "anteriores" mantém a posição de quem está lendo; "novas" desce só se a
 * pessoa já estava no fim.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../../../context/AuthContext";
import { lerMensagens } from "../../../services/centralProjetos";
import { juntarMensagens } from "../apresentacao";

/** Intervalo da busca de mensagens novas (sem martelar o servidor). */
export const INTERVALO_DAS_MENSAGENS_MS = 10000;

const ESTADO_INICIAL = Object.freeze({
  mensagens: [],
  mais: false,
  carregando: true,
  carregandoAnteriores: false,
  erro: "",
  erroDasNovas: "",
  motivo: "inicial",
});

function semRepetir(anteriores, atuais) {
  const vistos = new Set(atuais.map((m) => m.id));
  return [...anteriores.filter((m) => !vistos.has(m.id)), ...atuais];
}

function abaVisivel() {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

export default function useMensagensDaConversa(conversaId, habilitado) {
  const { request } = useAuth();
  const [estado, setEstado] = useState(() => ({ ...ESTADO_INICIAL, carregando: habilitado }));
  const atual = useRef(estado);
  atual.current = estado;
  const vivo = useRef(true);

  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  const carregar = useCallback(async () => {
    if (!habilitado) return;
    setEstado((e) => ({ ...e, carregando: true, erro: "" }));
    try {
      const resposta = await lerMensagens(request, { conversa: conversaId });
      if (!vivo.current) return;
      setEstado({
        ...ESTADO_INICIAL,
        mensagens: resposta?.mensagens || [],
        mais: Boolean(resposta?.mais),
        carregando: false,
      });
    } catch (erro) {
      if (vivo.current) {
        setEstado((e) => ({ ...e, carregando: false, erro: erro?.message || "Não foi possível ler as mensagens agora." }));
      }
    }
  }, [conversaId, habilitado, request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /** Relê a última página e junta o que for novo ou tiver mudado. */
  const atualizar = useCallback(async () => {
    const { carregando, carregandoAnteriores, erro } = atual.current;
    if (!habilitado || carregando || carregandoAnteriores || erro) return;
    try {
      const resposta = await lerMensagens(request, { conversa: conversaId });
      if (!vivo.current) return;
      setEstado((e) => {
        const mensagens = juntarMensagens(e.mensagens, resposta?.mensagens || []);
        if (mensagens === e.mensagens && !e.erroDasNovas) return e;
        return { ...e, mensagens, erroDasNovas: "", motivo: mensagens === e.mensagens ? e.motivo : "novas" };
      });
    } catch (falha) {
      if (vivo.current) {
        setEstado((e) => ({ ...e, erroDasNovas: falha?.message || "Não deu para buscar as mensagens novas agora." }));
      }
    }
  }, [conversaId, habilitado, request]);

  useEffect(() => {
    if (!habilitado) return undefined;
    const relogio = setInterval(() => {
      if (abaVisivel()) atualizar();
    }, INTERVALO_DAS_MENSAGENS_MS);
    return () => clearInterval(relogio);
  }, [atualizar, habilitado]);

  const carregarAnteriores = useCallback(async () => {
    const { mensagens, mais, carregando, carregandoAnteriores } = atual.current;
    if (!mais || carregando || carregandoAnteriores || !mensagens.length) return;
    setEstado((e) => ({ ...e, carregandoAnteriores: true, erro: "" }));
    try {
      const resposta = await lerMensagens(request, { conversa: conversaId, antes: mensagens[0].enviada_em });
      if (!vivo.current) return;
      setEstado((e) => ({
        ...e,
        mensagens: semRepetir(resposta?.mensagens || [], e.mensagens),
        mais: Boolean(resposta?.mais),
        carregandoAnteriores: false,
        motivo: "anteriores",
      }));
    } catch (erro) {
      if (vivo.current) {
        setEstado((e) => ({
          ...e,
          carregandoAnteriores: false,
          erro: erro?.message || "Não foi possível ler as mensagens anteriores.",
        }));
      }
    }
  }, [conversaId, request]);

  /** Troca campos de uma mensagem já na tela (ex.: o selo tirado), sem reler a página. */
  const trocarMensagem = useCallback((id, campos) => {
    setEstado((e) => ({
      ...e,
      motivo: "troca",
      mensagens: e.mensagens.map((m) => (m.id === id ? { ...m, ...campos } : m)),
    }));
  }, []);

  return { ...estado, carregarAnteriores, tentarDeNovo: carregar, atualizar, trocarMensagem };
}
